import { Link } from "react-router-dom";
import { useAuth, API } from "@/App";
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  ArrowRight, Shield, Heart, Brain, Sparkles, MessageSquare, Briefcase,
  Users, BookOpen, Target, Volume2, Globe, Check, X, ChevronDown,
  Fingerprint, Calendar, Zap, Star, Crown, Gem, Download, Play, Pause,
  PenLine, Loader2
} from "lucide-react";
import { cn } from "@/lib/utils";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

/* ─── Constants ─── */
const GUIDES = [
  { icon: Heart, name: "The Friend", desc: "Casual chat, emotional support, and a companion who remembers your story.", span: "md:col-span-4", accent: "from-rose-500/20 to-transparent" },
  { icon: Sparkles, name: "The Mentor", desc: "Wisdom tailored to your beliefs — from scripture to philosophy to humanist thought.", span: "md:col-span-4", accent: "from-amber-500/20 to-transparent" },
  { icon: MessageSquare, name: "The Social Guide", desc: "Role-play difficult conversations and navigate social dynamics with confidence.", span: "md:col-span-4", accent: "from-emerald-500/20 to-transparent" },
  { icon: Briefcase, name: "The Job Guardian", desc: "Resume mastery, interview prep, salary negotiation, and workplace navigation.", span: "md:col-span-4", accent: "from-violet-500/20 to-transparent" },
  { icon: Target, name: "The Life Coach", desc: "Transform goals into daily micro-actions. Stay accountable. Build momentum.", span: "md:col-span-4", accent: "from-cyan-500/20 to-transparent" },
  { icon: BookOpen, name: "The Storyteller", desc: "Personalized fables drawn from your cultural traditions. Wisdom through narrative.", span: "md:col-span-4", accent: "from-orange-500/20 to-transparent" },
];

const MENTORS = [
  "Rumi", "Cleopatra", "Marcus Aurelius", "Maya Angelou", "Confucius",
  "Frida Kahlo", "Leonardo da Vinci", "Nikola Tesla", "Hypatia",
  "Sun Tzu", "Marie Curie", "Shakespeare", "Buddha", "Socrates",
  "Ada Lovelace", "Ibn Battuta", "Harriet Tubman", "Lao Tzu",
  "Nelson Mandela", "Amelia Earhart", "Galileo", "Nefertiti",
];

const STATS = [
  { value: "6", label: "AI Guides" },
  { value: "22", label: "Wisdom Council Mentors" },
  { value: "113", label: "Languages" },
  { value: "24/7", label: "Always Available" },
];

const TIER_TABLE = {
  features: [
    { name: "Messages per day", free: "5", plus: "50", duo: "Unlimited", family: "Unlimited", platinum: "Unlimited" },
    { name: "AI Modules", free: "1", plus: "All 6", duo: "All 6", family: "All 6", platinum: "All 6" },
    { name: "Wisdom Council", free: "Preview", plus: "Full access", duo: "Full access", family: "Full access", platinum: "Full access" },
    { name: "Mood Tracking", free: "Basic", plus: "Advanced", duo: "Advanced", family: "Family", platinum: "Team-wide" },
    { name: "Wisdom Council", free: false, plus: true, duo: true, family: true, platinum: true },
    { name: "AI Memory", free: "7 days", plus: "Unlimited", duo: "Unlimited", family: "Unlimited", platinum: "Unlimited" },
    { name: "Voice (TTS)", free: "5/mo", plus: "20/mo", duo: "40/mo", family: "80/mo", platinum: "Unlimited" },
    { name: "Speech Input (STT)", free: true, plus: true, duo: true, family: true, platinum: true },
    { name: "Goals & Streaks", free: false, plus: true, duo: true, family: true, platinum: true },
    { name: "Smart Backup", free: false, plus: true, duo: true, family: true, platinum: true },
    { name: "Members", free: "1", plus: "1", duo: "2", family: "4", platinum: "Unlimited" },
    { name: "Admin Dashboard", free: false, plus: false, duo: false, family: false, platinum: true },
    { name: "Priority Support", free: false, plus: false, duo: false, family: false, platinum: true },
    { name: "Biometric Login", free: false, plus: true, duo: true, family: true, platinum: true },
    { name: "Cultural Awareness", free: "Standard", plus: "Ultra", duo: "Ultra", family: "Ultra", platinum: "Ultra" },
  ],
  tiers: [
    { key: "free", name: "Free", monthly: "$0", annual: "$0", icon: Sparkles, accent: "text-zinc-400" },
    { key: "plus", name: "Omni+", monthly: "$15.99", annual: "$182.99", icon: Star, accent: "text-red-500", badge: "Popular" },
    { key: "duo", name: "Duo", monthly: "$25.99", annual: "$296.99", icon: Users, accent: "text-emerald-500", desc: "2 members" },
    { key: "family", name: "Family", monthly: "$34.99", annual: "$398.99", icon: Heart, accent: "text-violet-500", desc: "4 members" },
    { key: "platinum", name: "Group", monthly: "$49.99", annual: "$569.99", icon: Crown, accent: "text-amber-500", badge: "Best Value", desc: "6 members" },
  ],
};

const ADDONS = [
  { name: "Voice Plans", price: "from $8.99/mo", desc: "100–400 spoken responses monthly, plus top-up packs", icon: Volume2 },
  { name: "Global Voice Pack", price: "$4.99/mo", desc: "TTS in 107+ premium languages for Wisdom Council", icon: Globe },
];

/* ─── Animated Number ─── */
function AnimStat({ value, label }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.5 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} className="text-center">
      <p className={cn("font-heading text-4xl sm:text-5xl font-light tracking-tight transition-all duration-700", visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4")}>
        {value}
      </p>
      <p className="text-xs sm:text-sm text-zinc-500 mt-1 uppercase tracking-widest">{label}</p>
    </div>
  );
}

/* ─── Scroll Reveal ─── */
function Reveal({ children, className, delay = 0 }) {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVis(true); }, { threshold: 0.15 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} className={cn("transition-all duration-700 ease-out", vis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8", className)} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

/* ─── Anthem Player ─── */
function AnthemPlayer() {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);

  const toggle = () => {
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); }
    else { audioRef.current.play().catch(() => {}); }
    setPlaying(!playing);
  };

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => setProgress(a.duration ? (a.currentTime / a.duration) * 100 : 0);
    const onEnd = () => { setPlaying(false); setProgress(0); };
    const onErr = () => {
      // Fallback: if API source fails, try local file
      if (a.src.includes("/api/")) {
        a.src = "/omni-anthem.mp3";
        a.load();
      }
    };
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("ended", onEnd);
    a.addEventListener("error", onErr);
    return () => { a.removeEventListener("timeupdate", onTime); a.removeEventListener("ended", onEnd); a.removeEventListener("error", onErr); };
  }, []);

  return (
    <div className="flex items-center gap-4">
      <audio ref={audioRef} src={`${API}/assets/anthem`} preload="metadata" />
      <button data-testid="anthem-play-btn" onClick={toggle} className="w-12 h-12 rounded-full flex items-center justify-center border transition-all hover:scale-105" style={{ borderColor: "rgba(220,38,38,0.3)", background: playing ? "rgba(220,38,38,0.15)" : "rgba(255,255,255,0.03)" }}>
        {playing ? <Pause className="w-5 h-5 text-red-500" /> : <Play className="w-5 h-5 text-red-500 ml-0.5" />}
      </button>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium mb-1.5">The Omni Manifesto</p>
        <div className="h-1 rounded-full overflow-hidden" style={{ background: "rgba(255,255,255,0.06)" }}>
          <div className="h-full rounded-full bg-red-500 transition-all duration-200" style={{ width: `${progress}%` }} />
        </div>
      </div>
    </div>
  );
}

/* ─── Review Modal ─── */
function ReviewModal({ open, onClose, token }) {
  const [form, setForm] = useState({ name: "", role: "", quote: "", rating: 5 });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  if (!open) return null;

  const submit = async () => {
    if (!form.quote.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/testimonials/submit`, form, { headers: { Authorization: `Bearer ${token}` } });
      setDone(true);
    } catch (e) {
      const msg = e.response?.data?.detail || "Could not submit review";
      alert(msg);
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.7)" }}>
      <div className="w-full max-w-md rounded-2xl p-6 border" data-testid="review-modal" style={{ background: "#0a0d16", borderColor: "rgba(255,255,255,0.1)" }}>
        {done ? (
          <div className="text-center py-6">
            <Check className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
            <p className="font-heading text-lg font-medium mb-1">Thank you!</p>
            <p className="text-sm text-zinc-400 mb-4">Your review is pending approval and will appear shortly.</p>
            <button onClick={onClose} className="px-6 py-2 rounded-full text-sm font-medium bg-red-600 text-white hover:bg-red-700 transition-colors">Done</button>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-5">
              <p className="font-heading text-base font-medium">Share your experience</p>
              <button onClick={onClose} className="text-zinc-600 hover:text-white transition-colors"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3">
              <div className="flex gap-3">
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Your name" className="flex-1 bg-transparent border rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-red-500/50" style={{ borderColor: "rgba(255,255,255,0.1)" }} />
                <input value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} placeholder="Role / Location" className="flex-1 bg-transparent border rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-red-500/50" style={{ borderColor: "rgba(255,255,255,0.1)" }} />
              </div>
              <div>
                <p className="text-xs text-zinc-500 mb-1.5">Rating</p>
                <div className="flex gap-1">
                  {[1,2,3,4,5].map(s => (
                    <button key={s} onClick={() => setForm({ ...form, rating: s })} data-testid={`review-star-${s}`}>
                      <Star className={cn("w-5 h-5 transition-colors", s <= form.rating ? "text-amber-500 fill-amber-500" : "text-zinc-700")} />
                    </button>
                  ))}
                </div>
              </div>
              <textarea value={form.quote} onChange={e => setForm({ ...form, quote: e.target.value })} placeholder="What makes Omni special to you?" rows={4} maxLength={300} className="w-full bg-transparent border rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-red-500/50 resize-none" style={{ borderColor: "rgba(255,255,255,0.1)" }} />
              <p className="text-[10px] text-zinc-700 text-right">{form.quote.length}/300</p>
              <button data-testid="review-submit-btn" onClick={submit} disabled={!form.quote.trim() || submitting} className="w-full py-2.5 rounded-full text-sm font-medium bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center justify-center gap-2">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                {submitting ? "Submitting..." : "Submit Review"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ─── Main Page ─── */
export default function LandingPage() {
  const { token } = useAuth();
  const knownUser = typeof window !== "undefined" ? localStorage.getItem("omni_known_user") : null;
  const [billing, setBilling] = useState("monthly");
  const [testimonials, setTestimonials] = useState([]);
  const [showReview, setShowReview] = useState(false);

  useEffect(() => {
    axios.get(`${API}/testimonials`).then(r => setTestimonials(r.data.testimonials || [])).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen" style={{ background: "#060912", color: "#fff" }}>
      {/* ══════════ NAVBAR ══════════ */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl border-b" style={{ background: "rgba(6,9,18,0.7)", borderColor: "rgba(255,255,255,0.06)" }}>
        <div className="max-w-7xl mx-auto flex items-center justify-between px-6 md:px-12 py-4">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: "rgba(220,38,38,0.12)" }}>
              <Sparkles className="w-4.5 h-4.5 text-red-500" strokeWidth={1.5} />
            </div>
            <span className="font-heading text-lg font-medium tracking-tight">Omni</span>
          </div>
          <div className="hidden md:flex items-center gap-8 text-sm text-zinc-400">
            <a href="#guides" className="hover:text-white transition-colors">Guides</a>
            <a href="#wisdom" className="hover:text-white transition-colors">Wisdom Council</a>
            <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>
            <a href="#features" className="hover:text-white transition-colors">Features</a>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSwitcher variant="landing" />
            <Link to={token ? "/chat" : "/auth"}>
              <button data-testid="header-cta-btn" className="px-5 py-2.5 rounded-full bg-red-600 hover:bg-red-500 text-white text-sm font-medium transition-all hover:shadow-lg hover:shadow-red-600/25">
                {token ? "Open App" : knownUser ? "Log In" : "Get Started"}
              </button>
            </Link>
          </div>
        </div>
      </nav>

      {/* ══════════ HERO ══════════ */}
      <section className="relative pt-36 pb-24 md:pt-44 md:pb-32 px-6 md:px-12 overflow-hidden">
        {/* Glow */}
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(circle at 50% 30%, rgba(220,38,38,0.10) 0%, transparent 55%)" }} />
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-red-500 mb-6">AI Life Companion</p>
          </Reveal>
          <Reveal delay={100}>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-light tracking-tighter leading-[1.05] mb-7">
              The one friend who's<br />
              <span className="text-red-500">always on your side</span>
            </h1>
          </Reveal>
          <Reveal delay={200}>
            <p className="text-base sm:text-lg text-zinc-400 max-w-2xl mx-auto mb-10 leading-relaxed">
              Six AI guides. Twenty-two Wisdom Council mentors. One hundred and thirteen languages. An angel on your shoulder that remembers who you are, respects where you come from, and grows alongside you.
            </p>
          </Reveal>
          <Reveal delay={300}>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link to={token ? "/chat" : "/auth"}>
                <button data-testid="hero-cta-btn" className="flex items-center gap-2 px-8 py-4 rounded-full bg-red-600 hover:bg-red-500 text-white font-medium text-base transition-all hover:shadow-xl hover:shadow-red-600/30 hover:-translate-y-0.5">
                  {token ? "Continue your journey" : knownUser ? "Welcome back — log in" : "Start your journey"} <ArrowRight className="w-5 h-5" />
                </button>
              </Link>
              <button onClick={() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" })} className="flex items-center gap-2 px-6 py-4 rounded-full text-zinc-400 hover:text-white text-sm transition-colors">
                View plans <ChevronDown className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs text-zinc-600 mt-5">Free forever. No credit card required.</p>
          </Reveal>
        </div>
      </section>

      {/* ══════════ STATS BAR ══════════ */}
      <section className="border-y px-6 md:px-12 py-12" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
        <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8">
          {STATS.map(s => <AnimStat key={s.label} {...s} />)}
        </div>
      </section>

      {/* ══════════ 7 AI GUIDES ══════════ */}
      <section id="guides" className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-red-500 mb-4">Modules</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-light tracking-tight mb-4">Six guides, one companion</h2>
            <p className="text-zinc-400 max-w-xl mb-14">Each module is a distinct AI personality, trained for a specific domain of your life. Switch freely or let Omni detect what you need.</p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
            {GUIDES.map((g, i) => {
              const Icon = g.icon;
              return (
                <Reveal key={g.name} delay={i * 60} className={cn("rounded-2xl p-7 border transition-all duration-300 hover:-translate-y-1 group", g.span)} style={{ background: "rgba(255,255,255,0.02)", borderColor: "rgba(255,255,255,0.07)" }}>
                  <div className={cn("w-11 h-11 rounded-xl flex items-center justify-center mb-5 bg-gradient-to-br", g.accent)}>
                    <Icon className="w-5 h-5 text-white/80" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-heading text-lg font-medium mb-2 tracking-tight">{g.name}{g.soon && <span className="ml-2 align-middle text-[9px] font-bold uppercase tracking-widest bg-blue-500/15 text-blue-400 px-2 py-0.5 rounded-full">Coming Soon</span>}</h3>
                  <p className="text-sm text-zinc-500 leading-relaxed">{g.desc}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ══════════ WISDOM COUNCIL ══════════ */}
      <section id="wisdom" className="py-24 md:py-32 overflow-hidden" style={{ background: "#0a0d16" }}>
        <div className="px-6 md:px-12 max-w-6xl mx-auto mb-14">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-red-500 mb-4">Wisdom Council</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-light tracking-tight mb-4">Counsel from the ages</h2>
            <p className="text-zinc-400 max-w-xl">Converse with 22 historical mentors — each with a unique voice and worldview. Ask Rumi about love. Challenge Socrates on ethics. Let Maya Angelou inspire your courage.</p>
          </Reveal>
        </div>
        {/* Marquee */}
        <div className="relative">
          <div className="flex gap-4 animate-marquee">
            {[...MENTORS, ...MENTORS].map((name, i) => (
              <div key={`${name}-${i}`} className="flex-shrink-0 px-6 py-3.5 rounded-xl border" style={{ background: "rgba(255,255,255,0.02)", borderColor: "rgba(255,255,255,0.07)" }}>
                <p className="text-sm font-medium whitespace-nowrap" style={{ fontFamily: "'Playfair Display', serif" }}>{name}</p>
              </div>
            ))}
          </div>
        </div>
        {/* 113 Languages */}
        <div className="px-6 md:px-12 max-w-6xl mx-auto mt-20">
          <Reveal>
            <div className="rounded-2xl p-8 md:p-12 border flex flex-col md:flex-row items-center gap-8" style={{ background: "rgba(255,255,255,0.02)", borderColor: "rgba(255,255,255,0.07)" }}>
              <div className="w-20 h-20 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: "rgba(220,38,38,0.1)" }}>
                <Globe className="w-10 h-10 text-red-500" strokeWidth={1} />
              </div>
              <div>
                <h3 className="font-heading text-2xl font-medium tracking-tight mb-2">113 Languages. Your mother tongue included.</h3>
                <p className="text-zinc-400 leading-relaxed">Six languages free. Over 100 premium languages with the Global Voice Pack. Omni doesn't just translate — it understands cultural context, greetings, and communication styles unique to your heritage.</p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══════════ FEATURES SPLIT ══════════ */}
      <section id="features" className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-red-500 mb-4">Capabilities</p>
            <h2 className="font-heading text-4xl sm:text-5xl font-light tracking-tight mb-14">Everything you need. Nothing you don't.</h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: Volume2, title: "Voice Conversations", desc: "Speak to Omni and hear responses read aloud. Natural, hands-free interaction." },
              { icon: Heart, title: "Mood Tracking", desc: "Log emotions daily. Visualize patterns with heatmaps and weekly insights." },
              { icon: Brain, title: "AI Memory", desc: "Omni remembers your preferences, your history, and your journey over time." },
              { icon: Target, title: "Goals & Streaks", desc: "Set personal goals with AI coaching. Build daily habits and stay accountable." },
              { icon: Calendar, title: "Daily Recaps", desc: "AI-written summaries of your day's conversations, moods, and progress." },
              { icon: Fingerprint, title: "Biometric Login", desc: "Fingerprint, Face ID, or Windows Hello. Secure access with zero friction." },
              { icon: Users, title: "Family Plans", desc: "One subscription, multiple members. Age-appropriate AI for every family member." },
              { icon: Zap, title: "Smart Greetings", desc: "Culturally-aware greetings in your native language, aware of time and occasion." },
              { icon: Download, title: "Smart Backup", desc: "Auto-backup conversations, memories, and mood logs. Export anytime, lose nothing." },
            ].map((f, i) => (
              <Reveal key={f.title} delay={i * 50} className="rounded-2xl p-6 border transition-all duration-300 hover:-translate-y-1" style={{ background: "rgba(255,255,255,0.02)", borderColor: "rgba(255,255,255,0.07)" }}>
                <f.icon className="w-5 h-5 text-red-500/80 mb-4" strokeWidth={1.5} />
                <h3 className="font-heading text-base font-medium mb-1.5 tracking-tight">{f.title}</h3>
                <p className="text-sm text-zinc-500 leading-relaxed">{f.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════ PRICING ══════════ */}
      <section id="pricing" className="py-24 md:py-32 px-6 md:px-12" style={{ background: "#0a0d16" }}>
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="text-center mb-12">
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-red-500 mb-4">Pricing</p>
              <h2 className="font-heading text-4xl sm:text-5xl font-light tracking-tight mb-4">Start free. Grow when ready.</h2>
              <p className="text-zinc-400 max-w-lg mx-auto mb-8">Every plan includes encrypted storage, cultural awareness, and 24/7 availability. Upgrade or downgrade anytime.</p>
              <div className="inline-flex items-center rounded-full p-1 border" style={{ borderColor: "rgba(255,255,255,0.1)", background: "rgba(255,255,255,0.03)" }}>
                <button data-testid="landing-billing-monthly" onClick={() => setBilling("monthly")} className={cn("px-5 py-2 rounded-full text-sm font-medium transition-all", billing === "monthly" ? "bg-red-600 text-white" : "text-zinc-400 hover:text-white")}>Monthly</button>
                <button data-testid="landing-billing-annual" onClick={() => setBilling("annual")} className={cn("px-5 py-2 rounded-full text-sm font-medium transition-all", billing === "annual" ? "bg-red-600 text-white" : "text-zinc-400 hover:text-white")}>
                  Annual <span className="text-[10px] ml-1 opacity-70">Save 17%</span>
                </button>
              </div>
            </div>
          </Reveal>

          {/* ── Tier Header Cards ── */}
          <Reveal>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
              {TIER_TABLE.tiers.map(t => {
                const Icon = t.icon;
                return (
                  <div key={t.key} data-testid={`landing-tier-${t.key}`} className={cn("relative rounded-2xl p-5 text-center border transition-all", t.badge ? "border-red-500/30" : "")} style={{ background: "rgba(255,255,255,0.02)", borderColor: t.badge ? undefined : "rgba(255,255,255,0.07)" }}>
                    {t.badge && <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full bg-red-600 text-[10px] font-bold uppercase tracking-wider">{t.badge}</span>}
                    <Icon className={cn("w-6 h-6 mx-auto mb-2", t.accent)} strokeWidth={1.5} />
                    <p className="font-heading text-sm font-medium mb-1">{t.name}</p>
                    <p className="font-heading text-2xl font-light tracking-tight">{billing === "monthly" ? t.monthly : t.annual}</p>
                    <p className="text-[10px] text-zinc-500">{billing === "monthly" ? "/month" : "/year"}</p>
                    <Link to="/auth">
                      <button className={cn("mt-4 w-full py-2 rounded-full text-xs font-medium transition-all", t.key === "free" ? "border text-zinc-400 hover:text-white" : "bg-red-600 hover:bg-red-500 text-white")} style={t.key === "free" ? { borderColor: "rgba(255,255,255,0.1)" } : {}}>
                        {t.key === "free" ? "Start Free" : "Subscribe"}
                      </button>
                    </Link>
                  </div>
                );
              })}
            </div>
          </Reveal>

          {/* ── Comparison Table ── */}
          <Reveal delay={100}>
            <div className="rounded-2xl border overflow-hidden" style={{ borderColor: "rgba(255,255,255,0.07)" }}>
              <div className="overflow-x-auto">
                <table data-testid="pricing-comparison-table" className="w-full min-w-[700px]">
                  <thead>
                    <tr style={{ background: "rgba(255,255,255,0.03)" }}>
                      <th className="text-left text-xs font-bold uppercase tracking-widest text-zinc-500 px-5 py-3.5 w-[200px]">Feature</th>
                      {TIER_TABLE.tiers.map(t => (
                        <th key={t.key} className={cn("text-center text-xs font-bold uppercase tracking-widest px-3 py-3.5", t.accent)}>{t.name}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {TIER_TABLE.features.map((f, i) => (
                      <tr key={f.name} style={{ borderTop: "1px solid rgba(255,255,255,0.04)", background: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.01)" }}>
                        <td className="px-5 py-3 text-sm text-zinc-300">{f.name}</td>
                        {["free", "plus", "duo", "family", "platinum"].map(tierKey => {
                          const val = f[tierKey];
                          return (
                            <td key={tierKey} className="text-center px-3 py-3">
                              {val === true ? <Check className="w-4 h-4 text-emerald-500 mx-auto" strokeWidth={2.5} /> :
                               val === false ? <X className="w-4 h-4 text-zinc-700 mx-auto" strokeWidth={2} /> :
                               <span className="text-sm text-zinc-400">{val}</span>}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </Reveal>

          {/* ── Add-Ons ── */}
          <Reveal delay={150}>
            <div className="mt-8">
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-zinc-500 mb-4 text-center">Power-Up Add-Ons</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
                {ADDONS.map(a => {
                  const Icon = a.icon;
                  return (
                    <div key={a.name} className="rounded-2xl p-5 border flex items-center gap-4" style={{ background: "rgba(255,255,255,0.02)", borderColor: "rgba(255,255,255,0.07)" }}>
                      <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "rgba(220,38,38,0.1)" }}>
                        <Icon className="w-5 h-5 text-red-500" strokeWidth={1.5} />
                      </div>
                      <div>
                        <p className="text-sm font-medium">{a.name} <span className="text-red-500 ml-1">{a.price}</span></p>
                        <p className="text-xs text-zinc-500">{a.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══════════ PRIVACY & TRUST ══════════ */}
      <section className="py-24 md:py-32 px-6 md:px-12">
        <div className="max-w-3xl mx-auto text-center">
          <Reveal>
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-8" style={{ background: "rgba(220,38,38,0.08)" }}>
              <Shield className="w-8 h-8 text-red-500" strokeWidth={1} />
            </div>
            <h2 className="font-heading text-4xl sm:text-5xl font-light tracking-tight mb-5">Your privacy is sacred</h2>
            <p className="text-zinc-400 leading-relaxed mb-10 max-w-xl mx-auto">
              No email required to start. Your conversations are encrypted. We cannot read your data. You own everything you share. Built for trust from day one.
            </p>
          </Reveal>
          <Reveal delay={100}>
            <div className="flex flex-wrap justify-center gap-3">
              {["Anonymous accounts", "Encrypted storage", "Data minimization", "GDPR compliant", "Biometric auth", "No tracking"].map(tag => (
                <span key={tag} className="px-4 py-2 rounded-full text-xs text-zinc-400 border" style={{ borderColor: "rgba(255,255,255,0.08)", background: "rgba(255,255,255,0.02)" }}>{tag}</span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══════════ TESTIMONIAL WALL ══════════ */}
      {testimonials.length > 0 && (
        <section className="py-24 md:py-32 px-6 md:px-12 overflow-hidden" data-testid="testimonial-wall">
          <div className="max-w-7xl mx-auto">
            <Reveal>
              <div className="text-center mb-14">
                <p className="text-xs font-bold uppercase tracking-[0.25em] text-red-500 mb-3">Voices</p>
                <h2 className="font-heading text-4xl sm:text-5xl font-light tracking-tight mb-4">Loved by people worldwide</h2>
                {token && (
                  <button data-testid="write-review-btn" onClick={() => setShowReview(true)} className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-xs font-medium border transition-all hover:bg-red-600 hover:text-white hover:border-red-600" style={{ borderColor: "rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.5)" }}>
                    <PenLine className="w-3.5 h-3.5" /> Write a Review
                  </button>
                )}
              </div>
            </Reveal>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {testimonials.map((t, i) => (
                <Reveal key={t.id || i} delay={i * 80}>
                  <div data-testid={`testimonial-${i}`} className="rounded-2xl p-6 border h-full flex flex-col" style={{ background: "rgba(255,255,255,0.02)", borderColor: "rgba(255,255,255,0.07)" }}>
                    <div className="flex gap-0.5 mb-4">
                      {Array.from({ length: t.rating || 5 }).map((_, s) => (
                        <Star key={s} className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />
                      ))}
                    </div>
                    <p className="text-sm text-zinc-300 leading-relaxed flex-1 mb-5">"{t.quote}"</p>
                    <div className="flex items-center gap-3 pt-4 border-t" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
                      <div className="w-9 h-9 rounded-full flex items-center justify-center text-xs font-medium" style={{ background: "rgba(220,38,38,0.1)", color: "#ef4444" }}>
                        {t.avatar_initials || t.name?.slice(0, 2).toUpperCase()}
                      </div>
                      <div>
                        <p className="text-sm font-medium">{t.name}</p>
                        {t.role && <p className="text-[11px] text-zinc-600">{t.role}</p>}
                      </div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ══════════ LAUNCH ANTHEM ══════════ */}
      <section className="py-16 px-6 md:px-12" style={{ background: "#0a0d16" }}>
        <div className="max-w-2xl mx-auto">
          <Reveal>
            <div className="rounded-2xl p-7 border" data-testid="anthem-section" style={{ background: "rgba(255,255,255,0.02)", borderColor: "rgba(255,255,255,0.07)" }}>
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-red-500 mb-4">Listen</p>
              <AnthemPlayer />
              <p className="text-[11px] text-zinc-700 mt-4">AI-narrated launch anthem — voiced by Omni</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══════════ FINAL CTA ══════════ */}
      <section className="py-24 md:py-32 px-6 md:px-12 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(circle at 50% 80%, rgba(220,38,38,0.08) 0%, transparent 50%)" }} />
        <div className="max-w-3xl mx-auto text-center relative z-10">
          <Reveal>
            <h2 className="font-heading text-4xl sm:text-5xl font-light tracking-tight mb-5">
              Your companion awaits
            </h2>
            <p className="text-zinc-400 mb-10 max-w-md mx-auto">
              Start with just a username. No email, no tracking, no judgment. Your AI, your rules.
            </p>
            <Link to="/auth">
              <button data-testid="bottom-cta-btn" className="px-10 py-4 rounded-full bg-red-600 hover:bg-red-500 text-white font-medium transition-all hover:shadow-xl hover:shadow-red-600/30 hover:-translate-y-0.5">
                Begin now <ArrowRight className="w-5 h-5 inline ml-2" />
              </button>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ══════════ FOOTER ══════════ */}
      <footer className="border-t px-6 md:px-12 py-16" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10 mb-12">
            <div>
              <div className="flex items-center gap-2.5 mb-4">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: "rgba(220,38,38,0.12)" }}>
                  <Sparkles className="w-4 h-4 text-red-500" strokeWidth={1.5} />
                </div>
                <span className="font-heading text-base font-medium">Omni</span>
              </div>
              <p className="text-xs text-zinc-600 leading-relaxed">The AI life companion that remembers, adapts, and grows with you.</p>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-3">Product</p>
              <div className="space-y-2 text-sm text-zinc-500">
                <a href="#guides" className="block hover:text-white transition-colors">AI Guides</a>
                <a href="#wisdom" className="block hover:text-white transition-colors">Wisdom Council</a>
                <a href="#features" className="block hover:text-white transition-colors">Features</a>
                <a href="#pricing" className="block hover:text-white transition-colors">Pricing</a>
              </div>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-3">Resources</p>
              <div className="space-y-2 text-sm text-zinc-500">
                <Link to="/guide" className="block hover:text-white transition-colors">Feature Guide</Link>
                <Link to="/gift" className="block hover:text-white transition-colors">Gift a Subscription</Link>
                <Link to="/gift/bulk" className="block hover:text-white transition-colors">Corporate Gifting</Link>
              </div>
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-3">Platform</p>
              <div className="space-y-2 text-sm text-zinc-500">
                <p>Web App (PWA)</p>
                <p>iOS (Coming Soon)</p>
                <p>Android (Coming Soon)</p>
                <Link to="/status" className="block hover:text-white transition-colors">System Status</Link>
                <Link to="/press" className="block hover:text-white transition-colors">Press Kit</Link>
              </div>
            </div>
          </div>
          <div className="border-t pt-8 flex flex-col md:flex-row items-center justify-between gap-4" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
            <p className="text-xs text-zinc-600">&copy; {new Date().getFullYear()} Omni. All rights reserved.</p>
            <p className="text-xs text-zinc-700 text-center">Omni is an AI companion, not a licensed professional. In a crisis, contact emergency services.</p>
          </div>
        </div>
      </footer>

      {/* ── Marquee CSS ── */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600&family=Playfair+Display:ital,wght@0,400;0,600;1,400&display=swap');
        .font-heading { font-family: 'Outfit', sans-serif; }
        @keyframes marquee { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        .animate-marquee { animation: marquee 40s linear infinite; display: flex; width: max-content; }
        .animate-marquee:hover { animation-play-state: paused; }
      `}</style>

      {/* Review Modal */}
      <ReviewModal open={showReview} onClose={() => setShowReview(false)} token={token} />
    </div>
  );
}
